import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Hero template — dramatic first impression.
 *
 *   ┌─────────────────────────────────────────┐
 *   │      (soft gradient banner strip)        │
 *   │                                          │
 *   │              [LARGE AVATAR]              │
 *   │                                          │
 *   │            USERNAME  badges              │
 *   │            description                   │
 *   │                                          │
 *   │              socials row                 │
 *   │              links stack                 │
 *   │              widgets                     │
 *   │              audio                       │
 *   │              meta                        │
 *   └─────────────────────────────────────────┘
 *
 * A banner strip caps the top of the card; the avatar sits half on it and
 * half below, like a profile hero banner. Everything underneath is
 * centered — this template is designed to showcase a single identity,
 * not pack info density.
 */
export function renderHero(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--hero");
  p.avatar.classList.add("avatar--hero");

  const banner = el("div", { class: "card__hero-banner", "aria-hidden": "true" });

  const identity = el("div", { class: "card__hero-identity" }, [
    p.avatar,
    el("div", { class: "card__name-row card__name-row--hero" }, [p.username, p.badges]),
    p.description,
  ]);

  const actionRow = el("div", { class: "card__hero-actions" });
  if (p.socials) actionRow.append(p.socials);

  const metaRow = el("div", { class: "card__meta card__meta--centered" });
  const vc = p.meta.getInline("views");    if (vc) metaRow.append(vc);
  const loc = p.meta.getInline("location"); if (loc) metaRow.append(loc);
  const clk = p.meta.getInline("clock");    if (clk) metaRow.append(clk);
  const ct = p.meta.getInline("contact");
  // Contact floats top-right in hero layout (matches banner aesthetic).
  if (ct && p.contact) {
    const corner = el("div", { class: "card__corner card__corner--top-right" }, [ct]);
    p.card.append(corner);
  }

  p.card.append(banner, identity);
  if (actionRow.childElementCount > 0) p.card.append(actionRow);
  if (p.widgets) p.card.append(p.widgets);
  if (p.links) p.card.append(p.links);
  if (p.audio) p.card.append(p.audio);
  if (metaRow.childElementCount > 0) p.card.append(metaRow);
  p.meta.applyCornerZones();

  return p.card;
}
